import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tagNameSchema } from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { Icon } from '../../common/Icon';
import { useAddTagMutation, useListTagsQuery } from '../tagEndpoints';

/**
 * QZ-D15, QZ-D40: the fixed, admin-curated tag list at `/tags`. No rename, no delete. An inline
 * add row sits above the tags as chips, and the tag just added is highlighted (QZ-D47).
 */
export const TagsPage = () => {
  const { t } = useTranslation();
  const { data, error: listError } = useListTagsQuery();
  const [addTag, { isLoading: adding }] = useAddTagMutation();

  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [added, setAdded] = useState<string | undefined>();

  const listCode = problemCode(listError);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    setAdded(undefined);

    const trimmed = name.trim();
    if (!tagNameSchema.safeParse(trimmed).success) {
      setError(t('error.InvalidTagName'));
      return;
    }

    try {
      await addTag({ name: trimmed }).unwrap();
      setName('');
      setAdded(trimmed);
    } catch (thrown) {
      const code = problemCode(thrown);
      setError(t(code ? `error.${code}` : 'error.unknown'));
    }
  };

  return (
    <main className="qz-main">
      <div className="qz-container is-narrow">
        <div className="qz-page-header">
          <div>
            <h1>{t('tag.page.heading')}</h1>
            <p className="qz-sub">{t('tag.page.sub')}</p>
          </div>
        </div>
        {listError && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>{t(listCode ? `error.${listCode}` : 'error.unknown')}</div>
          </div>
        )}
        <div className="qz-card">
          <div className="qz-card-body">
            <form className="d-flex gap-2 align-items-start" onSubmit={handleSubmit}>
              <div className="flex-grow-1">
                <label htmlFor="tag-name" className="visually-hidden">
                  {t('tag.page.name')}
                </label>
                <div className="input-group">
                  <span className="input-group-text">#</span>
                  <input
                    id="tag-name"
                    className={`form-control${error ? ' is-invalid' : ''}`}
                    placeholder={t('tag.page.placeholder')}
                    aria-describedby={error ? 'tag-name-error' : undefined}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                {error && (
                  <div id="tag-name-error" className="invalid-feedback d-block">
                    {error}
                  </div>
                )}
              </div>
              <button type="submit" className="btn btn-primary" disabled={adding}>
                <Icon name="plus" /> {t('tag.page.add')}
              </button>
            </form>
            {added && (
              <div className="alert alert-success mt-3 mb-0">
                <Icon name="circle-check" />
                <div>{t('tag.page.added')}</div>
              </div>
            )}
          </div>
          <div className="qz-card-footer" style={{ background: 'var(--qz-surface)' }}>
            <div className="qz-section-title">
              {t('tag.page.count', { count: data?.tags.length ?? 0 })}
            </div>
            <div className="qz-tags" style={{ gap: '0.5rem' }}>
              {(data?.tags ?? []).map((tag) => (
                <span key={tag} className={`qz-tag${tag === added ? ' is-new' : ''}`}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default TagsPage;
